import { useState } from "react";
import { Users, Mail, Lock, User, Phone, Heart, Stethoscope, ArrowRight, LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MembersPage = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [loggedInMember, setLoggedInMember] = useState(null);

  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register form state
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    phone: "",
    is_patient: false,
    is_caregiver: false,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/members/login`, {
        email: loginEmail,
        password: loginPassword,
      });

      setLoggedInMember(response.data.member);
      toast.success("Welcome back!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (registerData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...submitData } = registerData;
      await axios.post(`${API}/members/register`, submitData);

      toast.success("Registration successful! Please log in.");
      setActiveTab("login");
      setLoginEmail(registerData.email);
      setRegisterData({
        email: "",
        password: "",
        confirmPassword: "",
        first_name: "",
        last_name: "",
        phone: "",
        is_patient: false,
        is_caregiver: false,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setLoggedInMember(null);
    setLoginEmail("");
    setLoginPassword("");
    toast.success("You have been logged out");
  };

  if (loggedInMember) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50" data-testid="member-dashboard">
        <div className="max-w-4xl mx-auto px-6 py-24">
          <Card className="shadow-xl border-0">
            <CardContent className="p-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-teal-700" />
                </div>
                <h1 className="font-serif text-3xl font-bold text-slate-900 mb-2">
                  Welcome, {loggedInMember.first_name}!
                </h1>
                <p className="text-slate-600">{loggedInMember.email}</p>
              </div>

              <div className="bg-slate-50 rounded-lg p-6 mb-8">
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-4">Member Dashboard</h3>
                <p className="text-slate-600 mb-4">
                  Thank you for being a member of the Sickle Cell Association. Your support helps us continue our advocacy work.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded border border-slate-200 text-center">
                    <p className="text-2xl font-bold text-amber-700">Active</p>
                    <p className="text-sm text-slate-500">Membership Status</p>
                  </div>
                  <div className="bg-white p-4 rounded border border-slate-200 text-center">
                    <p className="text-2xl font-bold text-teal-700">0</p>
                    <p className="text-sm text-slate-500">Events Attended</p>
                  </div>
                  <div className="bg-white p-4 rounded border border-slate-200 text-center">
                    <p className="text-2xl font-bold text-slate-700">--</p>
                    <p className="text-sm text-slate-500">Member Since</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="logout-btn"
                >
                  Log Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="members-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Join Our Community
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Become a <span className="text-amber-700 italic">Member</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Join our community of advocates, patients, caregivers, and supporters dedicated to improving the lives of those affected by sickle cell disease.
            </p>
          </div>
        </div>
      </section>

      {/* Auth Forms Section */}
      <section className="py-16 bg-slate-50" data-testid="auth-section">
        <div className="max-w-lg mx-auto px-6">
          <Card className="shadow-xl border-0">
            <CardContent className="p-8">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-8">
                  <TabsTrigger value="login" data-testid="login-tab">
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Register
                  </TabsTrigger>
                </TabsList>

                {/* Login Form */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin}>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="login-email" className="text-sm font-medium text-slate-700">
                          Email Address
                        </Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="login-email"
                            type="email"
                            placeholder="your@email.com"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            className="pl-10 h-12"
                            required
                            data-testid="login-email-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="login-password" className="text-sm font-medium text-slate-700">
                          Password
                        </Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="login-password"
                            type="password"
                            placeholder="••••••••"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            className="pl-10 h-12"
                            required
                            data-testid="login-password-input"
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 font-sans font-bold uppercase text-sm"
                        data-testid="login-submit-btn"
                      >
                        {loading ? "Signing In..." : "Sign In"}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </TabsContent>

                {/* Register Form */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister}>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="first_name" className="text-sm font-medium text-slate-700">
                            First Name
                          </Label>
                          <div className="relative mt-1">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              id="first_name"
                              type="text"
                              placeholder="First"
                              value={registerData.first_name}
                              onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                              className="pl-10 h-12"
                              required
                              data-testid="register-firstname-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="last_name" className="text-sm font-medium text-slate-700">
                            Last Name
                          </Label>
                          <Input
                            id="last_name"
                            type="text"
                            placeholder="Last"
                            value={registerData.last_name}
                            onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                            className="h-12 mt-1"
                            required
                            data-testid="register-lastname-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="register-email" className="text-sm font-medium text-slate-700">
                          Email Address
                        </Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="register-email"
                            type="email"
                            placeholder="your@email.com"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            className="pl-10 h-12"
                            required
                            data-testid="register-email-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                          Phone (Optional)
                        </Label>
                        <div className="relative mt-1">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="(123) 456-7890"
                            value={registerData.phone}
                            onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                            className="pl-10 h-12"
                            data-testid="register-phone-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="register-password" className="text-sm font-medium text-slate-700">
                          Password
                        </Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="register-password"
                            type="password"
                            placeholder="••••••••"
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            className="pl-10 h-12"
                            required
                            data-testid="register-password-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">
                          Confirm Password
                        </Label>
                        <div className="relative mt-1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="confirm-password"
                            type="password"
                            placeholder="••••••••"
                            value={registerData.confirmPassword}
                            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                            className="pl-10 h-12"
                            required
                            data-testid="register-confirm-password-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-3 pt-2">
                        <Label className="text-sm font-medium text-slate-700">I am a:</Label>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_patient"
                            checked={registerData.is_patient}
                            onCheckedChange={(checked) => setRegisterData({ ...registerData, is_patient: checked })}
                            data-testid="is-patient-checkbox"
                          />
                          <label htmlFor="is_patient" className="text-sm text-slate-600 flex items-center gap-2">
                            <Heart className="w-4 h-4 text-amber-600" />
                            Sickle Cell Patient
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_caregiver"
                            checked={registerData.is_caregiver}
                            onCheckedChange={(checked) => setRegisterData({ ...registerData, is_caregiver: checked })}
                            data-testid="is-caregiver-checkbox"
                          />
                          <label htmlFor="is_caregiver" className="text-sm text-slate-600 flex items-center gap-2">
                            <Stethoscope className="w-4 h-4 text-teal-600" />
                            Caregiver / Family Member
                          </label>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-amber-700 hover:bg-amber-800 text-white py-6 font-sans font-bold uppercase text-sm mt-4"
                        data-testid="register-submit-btn"
                      >
                        {loading ? "Creating Account..." : "Create Account"}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-semibold text-slate-900">
              Member Benefits
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-slate-100">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-teal-700 mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">Community Access</h3>
                <p className="text-slate-600">Connect with other patients, caregivers, and advocates in our community.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-8 text-center">
                <Mail className="w-12 h-12 text-amber-700 mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">Newsletter Updates</h3>
                <p className="text-slate-600">Stay informed about legislative updates, events, and resources.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-8 text-center">
                <Heart className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">Event Invitations</h3>
                <p className="text-slate-600">Get exclusive access to member-only events and programs.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MembersPage;
